import { createFileRoute, Link } from '@tanstack/react-router'
import type { V2AdminListMatcherJobsResponses } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { useState } from 'react'
import { useAdminMatcherJobsQuery } from '../../features/admin/hooks/use-admin-api'

type SearchParams = {
  page?: number
  status?: string
}

export const Route = createFileRoute('/admin/matcher-jobs')({
  component: AdminMatcherJobsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    status: (search.status as string) || undefined,
  }),
})

type MatcherJobItem = V2AdminListMatcherJobsResponses[200]['data']['items'][number]

const LIMIT = 50
const MATCHER_JOB_STATUSES = ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'] as const
type MatcherJobStatus = (typeof MATCHER_JOB_STATUSES)[number]

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'secondary' | 'destructive'> = {
  PENDING: 'secondary',
  PROCESSING: 'warning',
  COMPLETED: 'success',
  FAILED: 'destructive',
}

function formatDate(date: string): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function AdminMatcherJobsPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [selectedStatus, setSelectedStatus] = useState<MatcherJobStatus | ''>(
    (search.status as MatcherJobStatus) ?? ''
  )
  const currentPage = search.page ?? 1

  const { data: response, isLoading } = useAdminMatcherJobsQuery(
    currentPage,
    selectedStatus || undefined
  )

  const items: MatcherJobItem[] = response?.data?.items ?? []
  const total: number = response?.data?.total ?? 0
  const totalPages = Math.ceil(total / LIMIT)

  const columns: TableColumn<MatcherJobItem>[] = [
    {
      header: 'Run ID',
      width: '120px',
      render: (item) => <span className="font-mono text-xs">{item.id.slice(0, 8)}</span>,
    },
    {
      header: 'Startup',
      render: (item) => (
        <Link
          to="/admin/startups/$id"
          params={{ id: item.startupId }}
          className="font-medium text-primary hover:underline"
        >
          {item.startupName ?? item.startupId.slice(0, 8)}
        </Link>
      ),
    },
    {
      header: 'Status',
      width: '120px',
      render: (item) => (
        <Badge variant={STATUS_VARIANT[item.status] ?? 'secondary'} size="sm">
          {item.status}
        </Badge>
      ),
    },
    {
      header: 'Trigger',
      width: '160px',
      render: (item) => <CellText className="font-mono text-xs">{item.triggerReason}</CellText>,
    },
    {
      header: 'Error',
      render: (item) =>
        item.error ? (
          <span className="block max-w-md truncate text-xs text-destructive" title={item.error}>
            {item.error}
          </span>
        ) : (
          <CellText>—</CellText>
        ),
    },
    {
      header: 'Matches',
      width: '72px',
      render: (item) => <CellText className="tabular-nums">{item.matchCount ?? '—'}</CellText>,
    },
    {
      header: 'Avg rerank',
      width: '96px',
      render: (item) => <CellText className="tabular-nums">{item.avgRerankScore ?? '—'}</CellText>,
    },
    {
      header: 'σ rerank',
      width: '80px',
      render: (item) => <CellText className="tabular-nums">{item.stdRerankScore ?? '—'}</CellText>,
    },
    {
      header: 'Created',
      width: '160px',
      render: (item) => <CellText>{formatDate(item.createdAt)}</CellText>,
    },
    {
      header: 'Updated',
      width: '160px',
      render: (item) => <CellText>{formatDate(item.updatedAt)}</CellText>,
    },
  ]

  return (
    <div className="w-full space-y-4">
      <div className="flex gap-2 flex-wrap">
        {(['', ...MATCHER_JOB_STATUSES] as const).map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={selectedStatus === s}
            onClick={() => {
              setSelectedStatus(s)
              void navigate({ search: { page: 1, status: s || undefined } })
            }}
            className={`rounded px-3 py-1 text-sm ${
              selectedStatus === s
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        emptyMessage="No matcher jobs found."
        keyExtractor={(item) => item.id}
      />

      <Pagination
        page={currentPage}
        totalPages={totalPages}
        onPageChange={(p) =>
          void navigate({ search: { page: p, status: selectedStatus || undefined } })
        }
      />
    </div>
  )
}
